import { useEffect, useState } from "react";
import { X, Copy, ExternalLink, Bell, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, inputCls, labelCls, cardCls } from "@/lib/api";
import { ReadOnlyNote } from "@/components/app/AppShell";

const STATUS_META = {
  awaiting_confirmation: { label: "Awaiting confirmation", cls: "bg-amber-100 text-amber-800" },
  alternative_proposed: { label: "Alternative proposed", cls: "bg-blue-100 text-blue-800" },
  confirmed: { label: "Confirmed", cls: "bg-guinep-deep text-white" },
  completed: { label: "Completed", cls: "bg-guinep-soft text-guinep-mid" },
  cancelled: { label: "Cancelled", cls: "bg-guinep-soft text-guinep-mute" },
  declined: { label: "Declined", cls: "bg-red-100 text-red-700" },
};

const fmtTime = (t) => {
  if (!t) return "Any time";
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

const fmtDay = (d) => {
  if (!d) return "";
  return new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
};

const normPhone = (p) => {
  const d = (p || "").replace(/\D/g, "").replace(/^0+/, "");
  if (d.length === 7) return `1268${d}`;
  if (d.length === 10) return `1${d}`;
  return d;
};

const CalendarTab = ({ business, reload, readOnly = false }) => {
  const [scope, setScope] = useState("today");
  const [appointments, setAppointments] = useState([]);
  const [today, setToday] = useState("");
  const [selected, setSelected] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    const data = await api(`/appointments?scope=${scope}`);
    setAppointments(data.appointments);
    setToday(data.today);
    setLoaded(true);
  };

  useEffect(() => {
    load().catch(() => toast.error("Could not load calendar."));
  }, [scope]);

  const refresh = () => {
    load();
    reload();
  };

  return (
    <div data-testid="calendar-tab">
      <h1 className="font-serif text-3xl font-medium tracking-tight text-guinep-ink">Calendar</h1>
      <p className="mt-1.5 text-sm text-guinep-mute">Requests and appointments, in order.</p>

      <div className="mt-4 flex rounded-full bg-guinep-soft p-1" data-testid="calendar-scope">
        {[["today", "Today"], ["upcoming", "Upcoming"]].map(([v, l]) => (
          <button
            key={v}
            data-testid={`scope-${v}`}
            onClick={() => setScope(v)}
            className={`flex-1 rounded-full py-1.5 text-[12px] font-semibold transition-colors ${scope === v ? "bg-white text-guinep-deep shadow-sm" : "text-guinep-mute hover:text-guinep-ink"}`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-2.5">
        {appointments.map((a) => (
          <button
            key={a.appointment_id}
            data-testid={`appt-${a.appointment_id}`}
            onClick={() => setSelected(a)}
            className={`${cardCls} flex w-full items-center gap-3 p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-sm`}
          >
            <div className="w-[74px] shrink-0">
              <p className="text-sm font-semibold text-guinep-ink">{fmtTime(a.time)}</p>
              <p className="text-[11px] text-guinep-mute">{scope === "today" ? "Today" : fmtDay(a.date).split(",")[0]}</p>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-guinep-ink">{a.client_name || "Client"}</p>
              <p className="truncate text-xs text-guinep-mute">{a.service_title}</p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1.5">
              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${STATUS_META[a.status]?.cls}`} data-testid="appt-status">
                {STATUS_META[a.status]?.label}
              </span>
              {(a.reminder_24h_due || a.reminder_2h_due) && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700">
                  <Bell size={10} /> Reminder due
                </span>
              )}
            </div>
          </button>
        ))}
        {loaded && appointments.length === 0 && (
          <p className="py-10 text-center text-sm text-guinep-mute" data-testid="calendar-empty">
            {scope === "today" ? "No appointments today" : "Nothing upcoming"}
          </p>
        )}
      </div>

      {scope === "today" && today && (
        <p className="mt-6 text-center text-[11px] text-guinep-mute/60">{fmtDay(today)}</p>
      )}

      {selected && (
        <AppointmentModal
          appointment={selected}
          business={business}
          onClose={() => setSelected(null)}
          onChanged={refresh}
          readOnly={readOnly}
        />
      )}
    </div>
  );
};

const MessageBox = ({ message, gcal, ics, clientPhone, readOnly = false }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Message copied");
    } catch (e) {
      toast.error("Long-press to copy the message.");
    }
  };
  const sendText = gcal ? `${message}\n\nAdd to Calendar: ${gcal}` : message;
  return (
    <div className="mt-4 rounded-xl border border-guinep-line bg-guinep-soft/60 p-4" data-testid="appt-message-box">
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-guinep-mute">WhatsApp message — ready to send</p>
      <p className="whitespace-pre-line text-[13px] leading-relaxed text-guinep-ink" data-testid="appt-message-text">{message}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button data-testid="appt-copy-message" onClick={copy} className="inline-flex items-center gap-1.5 rounded-lg bg-guinep-deep px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-guinep-mid">
          <Copy size={12} /> Copy message
        </button>
        {clientPhone && !readOnly && (
          <a data-testid="appt-send-whatsapp" href={`https://wa.me/${clientPhone}?text=${encodeURIComponent(sendText)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-wa/60 px-3.5 py-2 text-xs font-semibold text-wa-dark transition-colors hover:bg-wa/10">
            <ExternalLink size={12} /> Send in WhatsApp
          </a>
        )}
        {clientPhone && readOnly && (
          <button data-testid="appt-send-whatsapp" onClick={() => toast.info("Admin view — read only")} className="inline-flex items-center gap-1.5 rounded-lg border border-wa/60 px-3.5 py-2 text-xs font-semibold text-wa-dark/60">
            <ExternalLink size={12} /> Send in WhatsApp
          </button>
        )}
        {gcal && (
          <a data-testid="appt-gcal-link" href={gcal} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-guinep-line px-3.5 py-2 text-xs font-semibold text-guinep-ink transition-colors hover:bg-white">
            Google Calendar
          </a>
        )}
        {ics && (
          <a data-testid="appt-ics-link" href={ics} className="inline-flex items-center gap-1.5 rounded-lg border border-guinep-line px-3.5 py-2 text-xs font-semibold text-guinep-ink transition-colors hover:bg-white">
            Apple / Outlook (.ics)
          </a>
        )}
      </div>
    </div>
  );
};

const AppointmentModal = ({ appointment, business, onClose, onChanged, readOnly = false }) => {
  const [apt, setApt] = useState(appointment);
  const [mode, setMode] = useState(null);
  const [altDate, setAltDate] = useState(appointment.date);
  const [altTime, setAltTime] = useState("");
  const [daySchedule, setDaySchedule] = useState([]);
  const [message, setMessage] = useState(null);
  const [links, setLinks] = useState(null);
  const [clientPhone, setClientPhone] = useState(appointment.client_phone || "");
  const [busy, setBusy] = useState(false);
  const guard = (fn) => (readOnly ? () => toast.info("Admin view — read only") : fn);

  useEffect(() => {
    if (mode === "suggest" && altDate) {
      api(`/appointments/day?date=${altDate}`).then((d) => setDaySchedule(d.appointments)).catch(() => {});
    }
  }, [mode, altDate]);

  const act = async (path, body = null) => {
    setBusy(true);
    try {
      const res = await api(`/appointments/${apt.appointment_id}/${path}`, {
        method: "POST",
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      if (res.message) setMessage(res.message);
      if (res.gcal) setLinks({ gcal: res.gcal, ics: res.ics });
      const statusMap = { confirm: "confirmed", suggest: "alternative_proposed", decline: "declined", complete: "completed", cancel: "cancelled" };
      setApt((a) => ({ ...a, status: statusMap[path] || a.status, ...(body?.date && path === "confirm" ? { date: body.date, time: body.time } : {}), ...(path === "suggest" ? { alt_date: body.date, alt_time: body.time } : {}), ...(body?.client_phone ? { client_phone: normPhone(body.client_phone) } : {}) }));
      setMode(null);
      onChanged();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const meta = STATUS_META[apt.status] || {};
  const showActions = apt.status === "awaiting_confirmation" || apt.status === "alternative_proposed";

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:px-5" data-testid="appt-modal">
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-white p-6 sm:rounded-2xl">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl font-medium text-guinep-ink">{apt.service_title}</h3>
          <button onClick={onClose} data-testid="appt-close" className="text-guinep-mute hover:text-guinep-ink"><X size={18} /></button>
        </div>
        <span className={`mt-2 inline-block rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${meta.cls}`}>{meta.label}</span>
        {readOnly && <div><ReadOnlyNote /></div>}

        <dl className="mt-4 space-y-2 text-sm">
          {[
            ["Client", apt.client_name || "—"],
            ["WhatsApp", apt.client_phone ? `+${apt.client_phone}` : "Shared via WhatsApp"],
            ["Requested", `${fmtDay(apt.date)}${apt.time ? ` · ${fmtTime(apt.time)}` : ""}`],
            apt.alt_date && apt.status === "alternative_proposed" && ["Proposed", `${fmtDay(apt.alt_date)} · ${fmtTime(apt.alt_time)}`],
            ["Duration", apt.service_minutes ? `Approx. ${apt.service_minutes} min` : "—"],
            ["Client type", apt.client_type === "new" ? "New client" : "Existing client"],
            apt.note && ["Note", apt.note],
          ].filter(Boolean).map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 border-b border-guinep-line/50 pb-2">
              <dt className="text-guinep-mute">{k}</dt>
              <dd className="text-right font-medium text-guinep-ink">{v}</dd>
            </div>
          ))}
        </dl>

        {(showActions || apt.status === "confirmed") && (
          <div className="mt-3">
            <label className={labelCls}>WhatsApp number</label>
            <input data-testid="appt-client-phone" className={inputCls} value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} placeholder="12685550142" disabled={readOnly} />
          </div>
        )}

        {showActions && mode !== "suggest" && (
          <div className="mt-5 space-y-2.5" data-testid="appt-actions">
            <button data-testid="appt-confirm" onClick={guard(() => act("confirm", { client_phone: clientPhone }))} disabled={busy} className="w-full rounded-xl bg-guinep-deep py-3.5 text-sm font-semibold text-white transition-colors hover:bg-guinep-mid disabled:opacity-50">
              {apt.status === "alternative_proposed" ? `Confirm ${fmtTime(apt.alt_time)} on ${fmtDay(apt.alt_date).split(",")[0]}` : "Confirm"}
            </button>
            <div className="flex gap-2.5">
              <button data-testid="appt-suggest" onClick={guard(() => setMode("suggest"))} disabled={busy} className="flex-1 rounded-xl border border-guinep-line py-3 text-sm font-semibold text-guinep-ink transition-colors hover:bg-guinep-soft">
                Suggest another time
              </button>
              <button data-testid="appt-decline" onClick={guard(() => act("decline"))} disabled={busy} className="flex-1 rounded-xl border border-red-200 py-3 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50">
                Decline
              </button>
            </div>
          </div>
        )}

        {mode === "suggest" && (
          <div className="mt-5 space-y-4" data-testid="appt-suggest-form">
            {daySchedule.length > 0 && (
              <div className="rounded-xl bg-guinep-soft/70 px-4 py-3" data-testid="appt-day-schedule">
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-guinep-mute">Already on {fmtDay(altDate).split(",")[0]}</p>
                {daySchedule.map((d) => (
                  <p key={d.appointment_id} className="flex items-center gap-2 text-xs text-guinep-ink">
                    <Clock size={11} className="text-guinep-mid" /> {fmtTime(d.time)} — {d.service_title} ({STATUS_META[d.status]?.label})
                  </p>
                ))}
              </div>
            )}
            <div className="flex gap-3">
              <div className="flex-1">
                <label className={labelCls}>Alternative date</label>
                <input type="date" data-testid="appt-alt-date" className={inputCls} value={altDate} onChange={(e) => setAltDate(e.target.value)} />
              </div>
              <div className="flex-1">
                <label className={labelCls}>Alternative time</label>
                <input type="time" data-testid="appt-alt-time" className={inputCls} value={altTime} onChange={(e) => setAltTime(e.target.value)} />
              </div>
            </div>
            <div className="flex gap-2.5">
              <button onClick={() => setMode(null)} className="flex-1 rounded-xl border border-guinep-line py-3 text-sm font-semibold text-guinep-mute">Back</button>
              <button data-testid="appt-suggest-send" onClick={guard(() => act("suggest", { date: altDate, time: altTime, client_phone: clientPhone }))} disabled={busy || !altDate || !altTime} className="flex-[2] rounded-xl bg-guinep-deep py-3 text-sm font-semibold text-white transition-colors hover:bg-guinep-mid disabled:opacity-50">
                Prepare suggestion
              </button>
            </div>
          </div>
        )}

        {apt.status === "confirmed" && (
          <div className="mt-5 space-y-2.5">
            {(apt.reminder_24h_due || apt.reminder_2h_due) && (
              <button
                data-testid="appt-reminder"
                onClick={guard(() => act("reminder?kind=" + (apt.reminder_2h_due ? "2h" : "24h"), { client_phone: clientPhone }))}
                disabled={busy}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-amber-300 bg-amber-50 py-3 text-sm font-semibold text-amber-800"
              >
                <Bell size={14} /> Prepare {apt.reminder_2h_due ? "2-hour" : "24-hour"} reminder message
              </button>
            )}
            <div className="flex gap-2.5">
              <button data-testid="appt-complete" onClick={guard(() => act("complete"))} disabled={busy} className="flex-1 rounded-xl bg-guinep-deep py-3 text-sm font-semibold text-white transition-colors hover:bg-guinep-mid disabled:opacity-50">
                Mark completed
              </button>
              <button data-testid="appt-cancel" onClick={guard(() => act("cancel"))} disabled={busy} className="flex-1 rounded-xl border border-guinep-line py-3 text-sm font-semibold text-guinep-mute transition-colors hover:text-red-600">
                Cancel appointment
              </button>
            </div>
          </div>
        )}

        {message && <MessageBox message={message} gcal={links?.gcal} ics={links?.ics} clientPhone={apt.client_phone || normPhone(clientPhone)} readOnly={readOnly} />}
      </div>
    </div>
  );
};

export default CalendarTab;
